import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { Session, useCountryPriceTiers, useRecruitment } from '@app/api';
import { QuestionTooltip, TextLoadingPlaceholder } from 'components';
import { Trans, useTranslation } from 'react-i18next';
import { useTargetingPlanOffer, usePlan } from 'queries';
import {
  computeIncentive,
  NO_PRICE_GROUP,
  sumProducts,
} from 'lib/priceComputation';
import { getRelevantProducts, useRelevantPlanProducts } from 'lib/planAndOffer';
import { useMemo } from 'react';
import { PriceChips } from './PriceChips';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  spacer: {
    width: 0,
    height: theme.height.chips,
    marginRight: theme.marginBase * 2,
    marginLeft: theme.marginBase * 2,
    borderRight: {
      width: 1,
      color: theme.colors.grayBorder,
      style: 'solid',
    },
  },
  total: {
    ...theme.fonts.h5,
    color: theme.colors.inkSecondary,
  },
  price: {
    ...theme.fonts.h5,
    color: theme.colors.inkPrimary,
  },
  questionIcon: {
    marginLeft: theme.marginBase,
  },
  buttonIcon: {
    fontSize: theme.iconSize.regular,
    marginRight: theme.marginBase,
  },
}));

interface Props {
  session: Session;
  duration?: number;
  numberOfParticipantRequired?: number;
  countryKeys?: string[];
}

export const SessionPriceChips = ({
  session,
  numberOfParticipantRequired: numberOfPartProps,
  duration: durationProps,
  countryKeys: countryKeysProps,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { data: recruitment } = useRecruitment(session.recruitmentId);

  const numberOfParticipantRequired =
    numberOfPartProps || session.numberOfParticipantRequired;

  const duration = durationProps || session.duration;
  const countryKeys = countryKeysProps || recruitment?.countryKeys || [];

  const { data: plan, isLoading } = usePlan(session.planId);
  const offer = useTargetingPlanOffer(plan && plan.id);

  const countryPriceGroups = useCountryPriceTiers(countryKeys);

  const products = useRelevantPlanProducts(
    session.planId,
    session.id,
    countryKeys,
  );

  const totals = useMemo(() => {
    const groups = countryPriceGroups?.length
      ? countryPriceGroups
      : [NO_PRICE_GROUP];

    return groups.map((group) => {
      const groupProducts = getRelevantProducts(products, {
        countryPriceGroupIds: group === NO_PRICE_GROUP ? [] : [group],
      });

      const incentive = computeIncentive(groupProducts, duration);
      return sumProducts(
        groupProducts,
        numberOfParticipantRequired,
        duration,
        incentive,
      );
    });
  }, [products, countryPriceGroups, numberOfParticipantRequired, duration]);

  return (
    <div className={classes.container}>
      {isLoading && <TextLoadingPlaceholder sample="Mme Alexandra Raymond" />}
      {!!plan?.products && offer && (
        <>
          {totals.length > 1 ? (
            <PriceChips
              products={products.filter((l) => !l.countryPriceGroupId)}
              quantity={numberOfParticipantRequired}
              duration={duration}
              countries={countryKeys.length}
              tooltip
            />
          ) : (
            <PriceChips
              products={products}
              quantity={numberOfParticipantRequired}
              duration={duration}
              offerName={offer?.name}
              tooltip
            />
          )}

          <div className={classes.spacer} />
          <div className={classes.total}>
            {totals.length > 1 ? (
              <Trans
                i18nKey="editSession.price.totalMax"
                values={{ total: Math.max(...totals) }}
                components={{
                  price: <strong className={classes.price} />,
                }}
              />
            ) : (
              <Trans
                i18nKey="editSession.price.total"
                values={{ total: Math.max(...totals) }}
                components={{
                  price: <strong className={classes.price} />,
                }}
              />
            )}
          </div>
          <QuestionTooltip
            title={t('editSession.price.tooltips.total')}
            className={classes.questionIcon}
          />
        </>
      )}
    </div>
  );
};
